// MiscValue.js
// Copyright to Lab is Fun, Inc. All rights reserved. 2024.
import { AppObject, AppWord, CodeGroup, TopCode } from "./AppConst";
import IsValid from "./IsValid";

export default class ValueMisc {

    static isAnsungOrHaman(dcCd) {
        return dcCd===TopCode.DcCode.Adc982 || dcCd===TopCode.DcCode.Adc906;
    }
    static numFormat(number, length) {
        return number.toString().padStart(length,"0");
    }

    static getLastDate(year, monthIndex) {
        if(monthIndex < 0) {
            year--;
            monthIndex = 11;
        }
        else if(monthIndex > 11) {
            year++;
            monthIndex = 0;
        }
        return new Date(year, monthIndex + 1, 0).getDate();
    }
    
    static getDateArraysToDisplayLikeCalendar(year, monthIndex) {
        const result = [];
        const firstDate = new Date(year, monthIndex, 1);
        const firstWeekDay = firstDate.getDay();
        const lastDate = ValueMisc.getLastDate(year, monthIndex);
        let day = 1;
        while(day <= lastDate) {
            const week = [];
            let start = 0;
            if(day===1) {
                for(let i=0; i<firstWeekDay; i++) {
                    week.push(null);
                    start++;
                }
            }
            
            for(let i=start; i<7; i++) {
                if(day > lastDate) {
                    week.push(null);
                }
                else {
                    week.push(day);
                }
                day++;
            }
            result.push(week);
        }
        return result;
    }

    static dateToStrings(date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();
        return `${year}년 ${AppObject.months[month]} ${day}일`;
    }

    static getYMD(dateNullable) {
        const date = dateNullable || new Date();
        let year = date.getFullYear();
        let month = date.getMonth()+1;
        let day = date.getDate();
        return year + '-' + ValueMisc.numFormat(month, 2) + '-' + ValueMisc.numFormat(day, 2);
    }

    // Same as: ValueUtil.getYmdHmsInputText(new Date(), beforeSec)
    static getYmdHms(date, beforeSec) {
        var d = date ? date : new Date();
        if(beforeSec) {
            d.setTime(d.getTime() - beforeSec * 1000);
        }
        return(
            ValueMisc.getYMD(d) + ' '
            + ValueMisc.numFormat(d.getHours(), 2) + ':'
            + ValueMisc.numFormat(d.getMinutes(), 2) + ':'
            + ValueMisc.numFormat(d.getSeconds(), 2) );
    }

    static getDateAgo(daysAgo) {
        var d = new Date();
        d.setDate(d.getDate() - daysAgo);
        return d;
    }

    static addDays(date, days) {
        var d = new Date(date);
        d.setDate(d.getDate() + days);
        return d;
    }
    
    static getYmdBefore(beforeSec) {
        var d = new Date();
        d.setTime(d.getTime() - (beforeSec || 0) * 1000);
        return ValueMisc.getYMD(d);
    }

    static getDateFromYmd(ymd) {
        const ymdArr = ymd.split(/-0?/);
        return new Date(ymdArr[0], ymdArr[1]-1, ymdArr[2]);
    }

    static getFirtDateOfMonth(date) {
        const firstDate = date || new Date();
        firstDate.setDate(1);
        return  firstDate;
    }

    static getDays(ymdFrom, ymdTo) {
        const from = new Date(ymdFrom);
        const to = new Date(ymdTo);
        const diff = to.getTime() - from.getTime();
        return Math.ceil(diff / 86400000);
    }

    static isSameDate(ymd1, ymd2) {
        if(ymd1 && ymd2) {
            return ymd1.substring(0, 10) === ymd2.substring(0, 10);
        }
        return false;
    }

    static formatDuration(duration) {
        if(duration===undefined || duration===null) return null;
        if(duration < 1) return "0분";
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        const seconds = duration % 60;
      
        const parts = [];
        if (hours > 0) {
            parts.push(hours + "시간");
        }
        if (minutes > 0) {
            parts.push(minutes + "분");
        }
        if (seconds > 0 && hours < 1) {
            parts.push(seconds + "초");
        }
        return parts.join(" ");
    }

    static parseAndMinusToHhmm(fromYmdHms, toYmdHms, useSign) {
        // same as getDotStoreTime in Java
        if(!fromYmdHms || !toYmdHms) return 'X'; // string
        const fromDate = new Date(fromYmdHms);
        const toDate = new Date(toYmdHms);
        const diffMs = toDate.getTime() - fromDate.getTime();
        const diffMin = Math.floor(diffMs / 60000);
        const minus = diffMin < 0;
        const absMin = Math.abs(diffMin);
        const hours = Math.floor(absMin / 60);
        const minutes = absMin % 60;
        return (minus && useSign ? '-' : '') + ValueMisc.numFormat(hours, 2) + ':' + ValueMisc.numFormat(minutes, 2);
    }

    // Homeplus 시간엄수확인용.
    static getDotStoreTimeHit(arrTimeStr, inputTimeStr, hitAllowedNullable) {
        if(!arrTimeStr || !inputTimeStr) return 'X';

        const hitAllowed = hitAllowedNullable || 30; // minutes
        const arrTime = new Date(arrTimeStr);
        const inputTime = new Date(inputTimeStr);
        const diffMillis = inputTime - arrTime;
        const diffMinutes = Math.floor(diffMillis / 60000); // milliseconds to minutes
        if(diffMinutes >= -hitAllowed && diffMinutes <= hitAllowed) {
            return 'HIT';
        }

        const sign = diffMinutes < 0 ? '-' : '';
        const absMinutes = Math.abs(diffMinutes);
        const hours = Math.floor(absMinutes / 60);
        const minutes = absMinutes % 60;
        // return `${sign}${`${hours}`.padStart(2, '0')}:${`${minutes}`.padStart(2, '0')}`;
        return sign + (hours.toString().padStart(2, '0')) + ':' + (minutes.toString().padStart(2, '0'));
    };

    // For Homeplus report:
    static makeDurationHeader(records) {
        let min = '9999-12-31';
        let max = '0000-00-00';
        for(const rec of records) {
            if(rec.planDate < min) min = rec.planDate;
            if(rec.planDate > max) max = rec.planDate;
        }
        return [`조회기간: ${min} ~ ${max}`];
    };

    static isText(text) {
        return typeof text === 'string';
    }

    static int2str(num) {
        const minus = num < 0;
        var s = '' + Math.floor(Math.abs(num));
        var r = '';
        var add = '';
        while(s.length > 3) {
            r = s.substring(s.length-3) + add + r;
            s = s.substring(0, s.length-3);
            add = ',';
        }
        r = s + add + r;
        return minus ? '-' + r : r;
    }

    static float2str(num, round) {
        const minus = num < 0;
        var i = Math.floor(Math.abs(num));
        // var r = minus ? num + i : num - i;
        if(num === i) {
            return ValueMisc.int2str(num);
        }
        var rawStr = '' + num;
        const ri = rawStr.indexOf('.');
        var frac = ri < 0 ? '.0' : rawStr.substring(ri, ri+round+1);
        var rs = ValueMisc.int2str(i) + frac;

        return minus ? '-' + rs : rs;
    }

    static substr(text, indexStart, indexEnd, defaultText) {
        // like javascript substring()
        if(Boolean(text)) {
            return text.substring(indexStart, indexEnd);
        }
        return defaultText || '';
    }

    static getCodes(tree, groupCode) {
        for(const group of tree) {
            if(group.codeGp===groupCode) {
                return [...group.codes];
            }
        }
    }

    static makeCodesToSelectList(tree, groupCode, valueKey) {
        const codes = ValueMisc.getCodes(tree, groupCode);
        return codes.map((code)=>{
            const option = {label: code.codeName};
            option[valueKey] = code.codeId;
            return option;
        });
    }

    // -21, 1, 5, 6, 11, 12, off 와 같은 순으로 정렬. 모두 텍스트임.
    static sortTemperatureCodeOptions(codes, valueKey) {
        return codes.sort((a, b) => {
            const numA = /\w/.test(a[valueKey]) ? 99999 : parseInt(a[valueKey]);
            const numB = /\w/.test(b[valueKey]) ? 99999 : parseInt(b[valueKey]);
            return numA - numB;
        });
    }

    static getFirstCode(tree, groupCode) {
        const codes = ValueMisc.getCodes(tree, groupCode);
        return codes[0].codeId;
    }

    // TSR의 DC에 대해 기본 온도값을 가져옴. Express DC의 경우 transTypeCd에 따라 tempF, tempM, tempR를 설정해 줘야 함.
    static getTsrDefaultTemp(dcInfoListData, codeTree, tsrData) {
        const WORD_OFF = 'off';
        const dcInfo = dcInfoListData.records.find(dc => dc.dcCd === tsrData.dcCd);
        if(dcInfo) {
            if(dcInfo.dcTypeCd === 'E' && IsValid.string(tsrData.transTypeCd, 1, 5)) {
                const transTypes = ValueMisc.getCodes(codeTree, CodeGroup.TRANS_TYPE_CD);
                const tt = transTypes.find(t => t.codeId === tsrData.transTypeCd);
                if(tt) {
                    return {
                        fore: tt.code1,
                        middle: tt.code2,
                        rear: tt.code3
                    }
                }
            }
            return {
                fore: dcInfo.tempFore || WORD_OFF,
                middle: dcInfo.tempMid || WORD_OFF,
                rear: dcInfo.tempRear || WORD_OFF
            }
        }
        return {
            fore: WORD_OFF,
            middle: WORD_OFF,
            rear: WORD_OFF
        }
    };

    static isTimeOver(startSec, howMany) {
        return (new Date().getTime() / 1000 - startSec) > howMany;
    }

    static hhmmToMinute(hhmm) {
        if(!IsValid.hhmm4(hhmm)) return null;
        const hh = Number(hhmm.substring(0, 2));
        const mm = Number(hhmm.substring(2, 4));
        return hh * 60 + mm;
    }

    static minuteToHhmm(minute) {
        if(minute === null || minute === undefined || Number.isNaN(minute)) return null;
        const oneDay = 24 * 60;
        const wrapped = ((minute % oneDay) + oneDay) % oneDay;
        const hh = Math.floor(wrapped / 60).toString().padStart(2, '0');
        const mm = (wrapped % 60).toString().padStart(2, '0');
        return `${hh}${mm}`;
    };

    /**
     * hhmm 형식을 HH:MM:SS 형식으로 변환. 초는 항상 00으로.
     * @param {*} hhmm 
     */
    static hhmmToH_m_s(hhmm) {
        if(!IsValid.hhmm4(hhmm)) return null;
        const hh = hhmm.substring(0, 2);
        const mm = hhmm.substring(2, 4);
        return `${hh}:${mm}:00`;
    }

    static h_m_sToHhmm(h_m_s) {
        if(IsValid.hhColonMmSs(h_m_s)) {
            return h_m_s.substring(0, 5).replace(':', '');
        }
        return null;
    }

    static printUsage(rec, idx, value) { return value==='Y'?'사용':'사용중지'; }
    static printDate(rec, idx, value) { return value.substring(0,10); }

    static isStoreClassId(value) { return value === '6'; }
    static isTruckCompanyClassId(value) {return value === '7' || value === '8';}
    static isDcClassId(value) { return !ValueMisc.isStoreClassId(value) && !ValueMisc.isTruckCompanyClassId(value); }

    static isOlderInSec(secSince, criteria) {
        return (new Date().getTime() / 1000 - secSince) > criteria;
    }

    static parseHourMinSec(hms, separator) {
        if(hms) {
            let parts;
            if(separator) parts = hms.split(separator);
            else parts = [hms.substring(0,2), hms.substring(2,4), hms.substring(4,6)];
            return {
                hour: parts[0] ? parseInt(parts[0]) : 0,
                minute: parts[1] ? parseInt(parts[1]) : 0,
                second: parts[2] ? parseInt(parts[2]) : 0,
            }
        }
        return {hour:0, minute:0, second:0};
    }

    // useReactiveVar 사용하는 배열은 직접 push, slice, shift 등 할 수 없어서 이렇게 해줘야 함.
    static pushToRepoArray(arrayOfRepo, one, isUnshift) {
        const arr = arrayOfRepo.slice(0);
        if(isUnshift) arr.unshift(one);
        else arr.push(one);
        return arr;
    }

    // Naver
    static getNaverV2AddressString(obj) {
        if(obj.v2) {
            // address: {jibunAddress: '서울특별시 중구 소공동  ', roadAddress: ''}
            if(obj.v2.address) {
                if(IsValid.getUtf8BytesLength(obj.v2.address.roadAddress)) {
                    return obj.v2.address.roadAddress.trim();
                }
                else if(IsValid.getUtf8BytesLength(obj.v2.address.jibunAddress)) {
                    return obj.v2.address.jibunAddress.trim();
                }
            }
        }
        return '';
    }


    static degreesToRadians(degrees) {
        return (degrees * Math.PI) / 180;
    }
      
    static radiansToDegrees(radians) {
        return (radians * 180) / Math.PI;
    }
      
    static getPolygon(lat, lon, numPoints, meter) {
        if (numPoints < 3 || numPoints > 16) {
            return [];
        }

        const vertices = [];
        const earthRadius = 6371000; // Earth's radius in meters
        const tilt = numPoints===4 ? 45 : 0;
      
        for (let i = 0; i < numPoints; i++) {
            const angle = ( (i * 360) / numPoints + tilt ) % 360 ; // Calculate the angle between vertices
            const lat1 = ValueMisc.degreesToRadians(lat);
            const lon1 = ValueMisc.degreesToRadians(lon);
            const angularDistance = meter / earthRadius; // Convert meters to radians
        
            const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angularDistance) + Math.cos(lat1) * Math.sin(angularDistance) * Math.cos(ValueMisc.degreesToRadians(angle)));
            const lon2 = lon1 + Math.atan2(Math.sin(ValueMisc.degreesToRadians(angle)) * Math.sin(angularDistance) * Math.cos(lat1), Math.cos(angularDistance) - Math.sin(lat1) * Math.sin(lat2));
        
            vertices.push({
                lat: ValueMisc.radiansToDegrees(lat2),
                lon: ValueMisc.radiansToDegrees(lon2),
            });
        }
        const first = {...vertices[0]};
        vertices.push(first);
      
        return vertices;
    }

    static degreeToText(deg) {
        const d = Math.floor(deg);
        const minute = (deg - d) * 60;
        const m = Math.floor(minute);
        const s = Math.round((minute-m)*60);
        return `${d}도 ${m}분 ${s}초`;
    }

    // ------ Menu ------
    static searchMenu(menuUrl, menuTree) {
        for(const menu of menuTree) {
            if(menu.menuUrl === menuUrl) return menu;
            if(menu.children) {
                const result = ValueMisc.searchMenu(menuUrl, menu.children);
                if(result) return result;
            }
        }
        return null;
    }

    static searchParentMenu(svcUrl, menuTree) {
        /* menuTree.forEach((menu) => {
            if(menu.menuUrl===svcUrl) return menu;
            else if(menu.children) {
                const result = ValueMisc.searchParentMenu(svcUrl, menu.children);
                if(result) return result;
            }
        }); */
        for(const menu of menuTree) {
            if(menu.menuUrl === svcUrl) return menu;
            if(menu.children) {
                const result = ValueMisc.searchParentMenu(svcUrl, menu.children);
                if(result) return result;
            }
        }
        return null;
    }

    static findMenu(menuUrl, menuTree) {
        // return {menu, parent}. parent can be null.
        let menuFound = ValueMisc.searchMenu(menuUrl, menuTree);
        let parentMenu = null;
        if(menuFound) {
            if(menuFound.svcUrl !== menuUrl) {
                parentMenu = ValueMisc.searchParentMenu(menuFound.svcUrl, menuTree);
            }
        }
        return {menu:menuFound, parentMenu:parentMenu};
    }
    
    // ------ URL ------
    static getUrlBase () {
        let url = window.location.href;
        const idx = url.indexOf('#');
        if(idx>0) return url.substring(0, idx);
        return url;
    }

    static getUrlJsonParam() {
        const url = window.location.href;
        const idx = url.indexOf('#');
        if(idx>0) {
            const params = url.substring(idx+1).split('&');
            const result = {};
            for(const param of params) {
                const pair = param.split('=');
                result[pair[0]] = decodeURI(pair[1]);
            }
            if(result.hasOwnProperty('jx')) {
                return JSON.parse(result.jx);
            }
        }
        return {};
    }

    static makeJsonParamString(json) {
        return `jx=${encodeURI(JSON.stringify(json))}`;
    }

    static makeMapUrl(json) {
        const now = new Date().getTime();
        const url = ValueMisc.getUrlBase() + '?t=' + now
            + '#' + AppWord.MAP_SUB_URL;
        if(json) return url + '&' + ValueMisc.makeJsonParamString(json);
        return url;
    }

    static makeTsrUrl(json) {
        // http://localhost:3000/#view=printtsr
        const now = new Date().getTime();
        const url = ValueMisc.getUrlBase() + '?t=' + now
            + '#' + AppWord.TSR_PRINT_SUB_URL;
        if(json) return url + '&' + ValueMisc.makeJsonParamString(json);
        return url;
    }

    static cutUrlParam() {
        const url = window.location.href;

        const idx = url.indexOf('#');
        //if(idx>0) return url.substring(0, idx);
        //return url;
        if(idx>0) {
            const params = url.substring(idx+1).split('&');
            let newUrl = url.substring(0, idx + 1)
                + params.filter((param)=>param.indexOf('jx=')<0).join('&');
            window.history.pushState(null, null, newUrl);
        }
    }

    static getLocalStorageValue(key) {
        const text = localStorage.getItem(key);
        if(text) {
            const obj = JSON.parse(text);
            if(obj) return obj;
        }
        return {};
    }

    static saveLocalStorageValue(key, value) {
        localStorage.setItem(key, JSON.stringify(value));
    }

    static addLocalStorageValue(storageKey, key, value) {
        const obj = ValueMisc.getLocalStorageValue(storageKey);
        obj[key] = value;
        ValueMisc.saveLocalStorageValue(storageKey, obj);
    }

    static readLocalStorageValue(storageKey, key) {
        const obj = ValueMisc.getLocalStorageValue(storageKey);
        if(obj.hasOwnProperty(key)) return obj[key];
        return null;
    }

    static splitPhoneNumber(value) {
        const newSect = ['','',''];
        if(value && value.length>0) {
            let tel = value.trim();
            const arr = tel.split(/\D+/);
            if(arr.length >= 3) {
                for(let i=0; i<3; i++) {
                    newSect[i] = arr[i];
                }
            }
            else {
                tel = tel.replace(/-/g, '');
                newSect[0] = tel.substring(0, 3);
                if(tel.length > 3) {
                    newSect[1] = tel.substring(3, 7);
                    if(tel.length > 7) newSect[2] = tel.substring(7);
                }
            }
        }
        return newSect;
    }

    static vehicleSorter(a, b) {
        if(!a.vehPlates) {
            if(!b.vehPlates) return 0;
            return -1;
        }
        if(!b.vehPlates) return 1;
        const aNew = a.vehPlates.indexOf('신규') > -1;
        const bNew = b.vehPlates.indexOf('신규') > -1;
        if(aNew && !bNew) return 1;
        if(!aNew && bNew) return -1;
        return a.vehPlates.localeCompare(b.vehPlates);
    }

    static sanitizeFilename(filename) {
        if(filename) return filename.replace(/\s+/g, '_'); // 공백을 언더스코어로 대체;
        return null;
    }
}